import React, { useEffect, Suspense, lazy, useState } from 'react';
import { useUIContext } from './contexts/UIContext';
import { useAuthContext } from './contexts/AuthContext';
import { useProgressContext } from './contexts/ProgressContext';
import { useNavigationContext } from './contexts/NavigationContext';
import LoadingPage from './components/common/LoadingPage';
import PageErrorBoundary from './components/common/PageErrorBoundary';
import { appData } from './data';
import logger from './utils/logger';

// Lazy load pages for better performance
const OnboardingFlow = lazy(() => import('./pages/onboarding/OnboardingFlow'));
const PreLessonSignupPage = lazy(() => import('./pages/PreLessonSignupPage'));
const ParentalAuthPage = lazy(() => import('./pages/parent/ParentalAuthPage'));
const ForgotPasswordPage = lazy(() => import('./pages/parent/ForgotPasswordPage'));
const ProfilePickerPage = lazy(() => import('./pages/child/ProfilePickerPage'));
const AddNewProfilePage = lazy(() => import('./pages/parent/AddNewProfilePage'));
const AddCoursePage = lazy(() => import('./pages/onboarding/AddCoursePage'));
const DashboardPage = lazy(() => import('./pages/child/DashboardPage'));
const LearningPathPage = lazy(() => import('./pages/child/LearningPathPage'));
const LessonPage = lazy(() => import('./pages/child/LessonPage'));
const QuizPage = lazy(() => import('./pages/child/QuizPage'));
const QuizResultsPage = lazy(() => import('./pages/child/QuizResultsPage'));
const CulturalNotePage = lazy(() => import('./pages/child/CulturalNotePage'));
const StoryPage = lazy(() => import('./pages/child/StorybookPage'));
const StickerPage = lazy(() => import('./pages/child/StickerPage'));
const GameZonePage = lazy(() => import('./pages/child/GameZonePage'));
const PracticePage = lazy(() => import('./pages/child/PracticePage'));
const SettingsPage = lazy(() => import('./pages/parent/ParentalDashboard')); // Renamed from ParentalDashboard
const SupportPage = lazy(() => import('./pages/parent/SupportPage'));
const SubscriptionPage = lazy(() => import('./pages/parent/SubscriptionPage'));
const PostSubscribeOnboardingPage = lazy(() => import('./pages/parent/PostSubscribeOnboardingPage'));
const TrialCompleteTransitionPage = lazy(() => import('./pages/parent/TrialCompleteTransitionPage'));
const NotificationsPage = lazy(() => import('./pages/parent/NotificationsPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));

// Lazy load activity pages
const CategorySortActivity = lazy(() => import('./pages/activities/CategorySortActivity'));
const ColorMatchActivity = lazy(() => import('./pages/activities/ColorMatchActivity'));
const ListenFindActivity = lazy(() => import('./pages/activities/ListenFindActivity'));
const NumberMatchActivity = lazy(() => import('./pages/activities/NumberMatchActivity'));
const SentenceScrambleActivity = lazy(() => import('./pages/activities/SentenceScrambleActivity'));
const WhatAmIActivity = lazy(() => import('./pages/activities/WhatAmIActivity'));
const SimpleConversationActivity = lazy(() => import('./pages/activities/SimpleConversationActivity'));

// Lazy load analytics dashboard
const AnalyticsDashboard = lazy(() => import('./components/analytics/AnalyticsDashboard'));

// Lazy load modal test page
const ModalTestPage = lazy(() => import('./components/modals/ModalTestPage'));

// Not Found page for invalid routes
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const PageRouter = () => {
    const { user, isLoading: isLoadingAuth } = useAuthContext();
    const { progress, isLoading: isLoadingProgress } = useProgressContext();
    const uiContext = useUIContext();
    const { page, setActiveModule, activeModule, setAuthAction, authAction } = uiContext;
    
    // Debug: Check what we're getting from UIContext
    logger.info('PageRouter', `Current page state: type=${typeof page}, value=${page}`);
    const { navigateTo } = useNavigationContext();
    
    // Add fallback state to prevent infinite loading
    const [fallbackTriggered, setFallbackTriggered] = useState(false);
    const [routingAttempts, setRoutingAttempts] = useState(0);
    
    // State to preserve the intended destination URL from initial page load
    const [intendedDestination, setIntendedDestination] = useState(null);
    const [hasNavigatedToDestination, setHasNavigatedToDestination] = useState(false);

    // Capture the URL path on initial mount (before auth/progress loads)
    useEffect(() => {
        const path = window.location.pathname;
        const urlPage = path === '/' ? 'dashboard' : path.substring(1).replace(/-/g, '_');
        
        // Check for URL parameters that indicate sign-in intent
        const urlParams = new URLSearchParams(window.location.search);
        const action = urlParams.get('action');
        
        // If URL has action=login parameter, set auth action to login
        if (action === 'login') {
            logger.info('PageRouter', 'URL contains action=login, setting auth action');
            setAuthAction('login');
        }
        
        // Map URL to valid page names
        const validPages = [
            'dashboard', 'learning_path', 'practice', 'game_zone', 'sticker_book', 
                   'storybook', 'profile', 'settings', 'parental_auth',
            'prelesson_signup', 'lesson', 'quiz', 'quiz_results', 'profile_picker', 'add_new_profile',
            'forgot_password', 'support_page', 'subscription_page', 'post_subscribe_onboarding',
            'trial_complete_transition', 'notifications', 'unsubscribe', 'analytics_dashboard',
            'modal_test', 'categorySort_activity', 'colorMatch_activity', 'listenFind_activity',
            'numberMatch_activity', 'sentenceScramble_activity', 'whatAmI_activity',
            'simpleConversation_activity', 'add_course'
        ];
        
        // Only preserve if it's a valid page and not loading/onboarding
        if (validPages.includes(urlPage) && urlPage !== 'loading' && urlPage !== 'onboarding') {
            logger.info('PageRouter', `Preserving intended destination from URL: ${urlPage}`);
            setIntendedDestination(urlPage);
        }
    }, []); // Empty dependency array - run only once on mount

    // Add timeout fallback to prevent infinite loading
    useEffect(() => {
        const loadingTimeout = setTimeout(() => {
            if (isLoadingAuth || isLoadingProgress) {
                logger.warn('PageRouter', 'Loading timeout after 15 seconds, triggering fallback');
                setFallbackTriggered(true);
            }
        }, 15000); // 15 second timeout

        return () => clearTimeout(loadingTimeout);
    }, [isLoadingAuth, isLoadingProgress]);

    // Reset fallback when loading completes
    useEffect(() => {
        if (!isLoadingAuth && !isLoadingProgress && fallbackTriggered) {
            logger.info('PageRouter', 'Loading complete, resetting fallback and routing attempts');
            setFallbackTriggered(false);
            setRoutingAttempts(0);
        }
    }, [isLoadingAuth, isLoadingProgress, fallbackTriggered]);

    // Reset routing attempts when page successfully changes
    useEffect(() => {
        const timer = setTimeout(() => {
            if (routingAttempts > 0) {
                logger.info('PageRouter', 'Page state stable for 2 seconds, resetting routing attempts');
                setRoutingAttempts(0);
            }
        }, 2000); // Reset after 2 seconds of stable page state

        return () => clearTimeout(timer);
    }, [page, routingAttempts]);

    useEffect(() => {
        if (isLoadingAuth || isLoadingProgress) {
            return; // Wait for all data
        }

        // Check if onboarding was just completed - if so, wait for progress to load
        const justCompletedOnboarding = localStorage.getItem('justCompletedOnboarding');
        if (justCompletedOnboarding) {
            const completedTime = parseInt(justCompletedOnboarding);
            const timeSinceCompletion = Date.now() - completedTime;
            // 🔧 MOBILE FIX: Extended wait time for mobile Firestore sync (10s instead of 5s)
            if (timeSinceCompletion < 10000 && !progress?.onboardingComplete) {
                // Onboarding was just completed, but progress hasn't loaded yet
                // Don't redirect - let the loading screen show
                logger.info('PageRouter', `Waiting for progress to sync after onboarding (${timeSinceCompletion}ms elapsed)`);
                return;
            }
        }

        logger.info('PageRouter', `Routing logic - current page: ${page}, has navigated: ${hasNavigatedToDestination}, intended: ${intendedDestination}`);

        // Prevent infinite loops by limiting navigation attempts
        if (routingAttempts > 5) {
            logger.warn('PageRouter', 'Too many routing attempts (>5), stopping to prevent infinite loop');
            return;
        }

        // Check if this is a valid page that exists in our pageMap
        const validPages = [
            'dashboard', 'learning_path', 'practice', 'game_zone', 'sticker_book', 
                   'storybook', 'profile', 'settings', 'parental_auth',
            'lesson', 'quiz', 'quiz_results', 'profile_picker', 'add_new_profile',
            'forgot_password', 'support_page', 'subscription_page', 'post_subscribe_onboarding',
            'trial_complete_transition', 'notifications', 'unsubscribe', 'analytics_dashboard',
            'modal_test', 'categorySort_activity', 'colorMatch_activity', 'listenFind_activity',
            'numberMatch_activity', 'sentenceScramble_activity', 'whatAmI_activity',
            'simpleConversation_activity', 'onboarding'
        ];

        const activeProfile = progress?.profiles?.[progress.lastActiveProfileId];
        
        // 🛡️ PRIORITY CHECK: If authInProgress is set and progress shows onboarding complete,
        // immediately navigate to dashboard (prevents onboarding page flash during magic link sign-in)
        const authInProgress = localStorage.getItem('authInProgress');
        if (authInProgress && progress && progress.onboardingComplete) {
            logger.info('PageRouter', 'Auth complete with existing progress, redirecting to dashboard');
            localStorage.removeItem('authInProgress');
            if (page !== 'dashboard' && page !== 'learning_path') {
                navigateTo('dashboard');
            }
            return;
        }
        
        // If the current page is valid and user has completed onboarding, don't redirect
        // BUT: Check if they need FTUE setup first (lesson page needs activeModule)
        if (progress && progress.onboardingComplete) {
            if (page === 'onboarding' || page === 'loading') {
                // Check if there's an intended destination from URL (e.g., email CTA)
                const destination = intendedDestination && intendedDestination !== 'onboarding' 
                    ? intendedDestination 
                    : 'dashboard';
                logger.info('PageRouter', `Onboarding complete but on onboarding/loading page, navigating to: ${destination}`);
                
                setRoutingAttempts(prev => prev + 1);
                navigateTo(destination);
                return;
            } else if (validPages.includes(page)) {
                // CRITICAL: If on storybook page without activeModule, set up trial bonus story
                // This handles users arriving from email CTAs ("Try the Bonus Story")
                if (page === 'storybook' && !activeModule && activeProfile) {
                    logger.info('PageRouter', 'On storybook page without activeModule, setting up trial bonus story');
                    const language = activeProfile.currentLanguage;
                    
                    // Use correct story IDs (from data files, not module IDs)
                    const storyId = language === 'Twi' 
                        ? 'ananse_and_the_pot_of_wisdom' 
                        : 'ijapa_and_the_borrowed_feathers';
                    
                    const moduleId = language === 'Twi' 
                        ? 'twi_m1_story_ananse' 
                        : 'yoruba_m1_story_ijapa';
                    
                    // Get story data
                    const languageData = appData[language];
                    const story = languageData?.storybooks?.[storyId];
                    
                    if (story) {
                        logger.info('PageRouter', `Loading story: ${storyId}, pages: ${story.pages?.length}`);
                        setActiveModule({ 
                            ...story, 
                            id: moduleId,  // Module ID for completion tracking
                            title: story.title, 
                            type: 'story',
                            storyId: storyId,  // Critical: story ID to load content
                            unit: 1,
                            isPreview: true,
                            accessLevel: 'taster'
                        });
                        return; // Wait for next render with activeModule set
                    } else {
                        // If story data couldn't be loaded, redirect to dashboard
                        logger.error('PageRouter', 'Story data not found, redirecting to dashboard');
                        navigateTo('dashboard');
                        return;
                    }
                }
                
                logger.info('PageRouter', 'On valid page with onboarding complete, no navigation needed');
                return;
            }
        }

        // 1. User has NOT completed onboarding
        if (!progress || !progress.onboardingComplete) {
            // If user is signed out, check if they should go to trial or login
            if (!user) {
                // Check if magic link authentication is in progress
                const urlParams = new URLSearchParams(window.location.search);
                const hasMagicLinkParams = urlParams.get('magicLink') === 'true' || 
                                          (urlParams.get('apiKey') && urlParams.get('oobCode'));
                
                // 🎯 Check for startFirstLesson intent from welcome email
                const startFirstLesson = urlParams.get('startFirstLesson') === 'true';
                
                // 🛡️ Check if any authentication is in progress (magic link, Google, etc.)
                const authInProgress = localStorage.getItem('authInProgress');
                
                // If startFirstLesson is detected and user is not authenticated, preserve intent
                if (startFirstLesson && !authInProgress && !hasMagicLinkParams) {
                    logger.info('PageRouter', 'Start first lesson intent detected from welcome email');
                    logger.info('PageRouter', 'Storing post-auth intent: startFirstLesson');
                    logger.info('PageRouter', 'Marking as returning user from email');
                    
                    // Store the intent so it can be restored after authentication
                    localStorage.setItem('postAuthIntent', 'startFirstLesson');
                    
                    // Store a flag indicating this is an existing user (for messaging)
                    localStorage.setItem('isReturningFromEmail', 'true');
                    
                    // Route to pre-lesson signup page (which has magic link + Google sign-in)
                    if (page !== 'prelesson_signup' && page !== 'loading') {
                        setRoutingAttempts(prev => prev + 1);
                        navigateTo('prelesson_signup');
                    }
                    return;
                }
                
                // If authentication is being processed, show loading page
                if (hasMagicLinkParams || authInProgress) {
                    logger.info('PageRouter', 'Magic link or auth in progress, showing loading page');
                    if (page !== 'loading') {
                        navigateTo('loading');
                    }
                    return; // Stay on loading page during auth flow
                }
                
                const allowedPages = ['parental_auth', 'forgot_password', 'onboarding', 'prelesson_signup', 'loading'];
                if (!allowedPages.includes(page)) {
                    setRoutingAttempts(prev => prev + 1);
                    // Use the authAction that was set from URL parameters
                    if (authAction === 'login') {
                        // Explicit login intent - go to unified auth page (magic link + Google)
                        logger.info('PageRouter', 'Explicit login action detected, navigating to prelesson_signup');
                        navigateTo('prelesson_signup');
                    } else if (hasMagicLinkParams) {
                        // Magic link in progress - show loading page
                        logger.info('PageRouter', 'Magic link params detected, showing loading page');
                        navigateTo('loading');
                    } else {
                        // No explicit login intent - allow trial flow (go to onboarding/welcome carousel)
                        navigateTo('onboarding');
                    }
                }
                return;
            }
            
            // If user is signed in but hasn't completed onboarding, show onboarding (welcome carousel)
            // BUT: Check if magic link is being processed first
            const urlParams = new URLSearchParams(window.location.search);
            const hasMagicLinkParams = urlParams.get('magicLink') === 'true' || 
                                      (urlParams.get('apiKey') && urlParams.get('oobCode'));
            
            // 🛡️ Check if any authentication is in progress
            const authInProgress = localStorage.getItem('authInProgress');
            
            // 🔧 If authentication is in progress, show loading page (don't navigate to onboarding)
            if (hasMagicLinkParams || authInProgress) {
                logger.info('PageRouter', 'Auth in progress for signed-in user, showing loading page');
                if (page !== 'loading' && page !== 'dashboard' && page !== 'lesson') {
                    navigateTo('loading');
                }
                return; // Stay until App.js completes and navigates appropriately
            }
            
            const allowedPages = ['onboarding', 'parental_auth', 'forgot_password', 'loading'];
            if (!allowedPages.includes(page)) {
                setRoutingAttempts(prev => prev + 1);
                navigateTo('onboarding');
            } else if (page === 'loading' && !hasMagicLinkParams && !authInProgress) {
                // If on loading page but no magic link/auth in progress, redirect to onboarding
                navigateTo('onboarding');
            }
            return;
        }

        // FTUE logic removed - users are now handled by PreLessonSignupPage
        // New users go directly from signup → lesson page (Case 2)
        // Returning users go directly from signup → dashboard (Case 2)

        // 2. Fully onboarded user (anonymous or authenticated)
        
        // CRITICAL: If on lesson page without valid activeModule, redirect to dashboard
        // This check must run EVERY time, regardless of navigation state
        if (page === 'lesson') {
            if (!activeModule) {
                logger.info('PageRouter', 'On lesson page without activeModule, redirecting to dashboard');
                setRoutingAttempts(prev => prev + 1);
                navigateTo('dashboard');
                return;
            } else {
                logger.info('PageRouter', `On lesson page with activeModule: ${activeModule?.id}, content length: ${activeModule?.content?.length}`);
            }
        }
        
        // For fully onboarded users, don't try to preserve "lesson" as a destination
        // because we can't restore which specific lesson they were on
        // Instead, redirect to dashboard or learning path
        const nonAppPages = ['onboarding', 'loading', 'parental_auth'];
        
        // Navigate to preserved destination if on a non-app page
        if (nonAppPages.includes(page)) {
            if (!hasNavigatedToDestination) {
                // 🎯 Check for post-auth intent (e.g., from welcome email)
                const postAuthIntent = localStorage.getItem('postAuthIntent');
                
                let destination;
                
                if (postAuthIntent === 'startFirstLesson') {
                    logger.info('PageRouter', 'Post-auth intent: startFirstLesson, routing returning user to dashboard');
                    destination = 'dashboard'; // Returning users go to dashboard, not first lesson
                    // Clear the intent so it doesn't persist
                    localStorage.removeItem('postAuthIntent');
                } else {
                    destination = intendedDestination === 'lesson' ? 'dashboard' : (intendedDestination || 'dashboard');
                }
                
                logger.info('PageRouter', `Navigating from non-app page to destination: ${destination}`);
                setHasNavigatedToDestination(true);
                setRoutingAttempts(prev => prev + 1);
                navigateTo(destination);
                return;
            } else {
                // Already navigated once, but still on non-app page - navigate to dashboard as fallback
                logger.info('PageRouter', 'Already navigated once but still on non-app page, fallback to dashboard');
                setRoutingAttempts(prev => prev + 1);
                navigateTo('dashboard');
                return;
            }
        }
        
        // If an already authenticated user lands on forgot_password page, send them to the dashboard.
        if (page === 'forgot_password' && user) {
            logger.info('PageRouter', 'Authenticated user on forgot_password page, redirecting to dashboard');
            setRoutingAttempts(prev => prev + 1);
            navigateTo('dashboard');
            return;
        }

    }, [isLoadingAuth, isLoadingProgress, user, progress, page, navigateTo, setActiveModule, activeModule, intendedDestination, hasNavigatedToDestination, routingAttempts]);

    const pageMap = {
        'loading': <LoadingPage />,
        'onboarding': <PageErrorBoundary pageName="Onboarding"><OnboardingFlow /></PageErrorBoundary>,
        'prelesson_signup': <PageErrorBoundary pageName="Pre-Lesson Signup"><PreLessonSignupPage /></PageErrorBoundary>,
        'parental_auth': <PageErrorBoundary pageName="Parental Authentication"><ParentalAuthPage /></PageErrorBoundary>,
        'forgot_password': <PageErrorBoundary pageName="Forgot Password"><ForgotPasswordPage /></PageErrorBoundary>,
        'profile_picker': <PageErrorBoundary pageName="Profile Picker"><ProfilePickerPage /></PageErrorBoundary>,
        'add_new_profile': <PageErrorBoundary pageName="Add New Profile"><AddNewProfilePage /></PageErrorBoundary>,
        'add_course': <PageErrorBoundary pageName="Add Course"><AddCoursePage /></PageErrorBoundary>,
        'dashboard': <PageErrorBoundary pageName="Dashboard"><DashboardPage /></PageErrorBoundary>,
        'learning_path': <PageErrorBoundary pageName="Learning Path"><LearningPathPage /></PageErrorBoundary>,
        'lesson': <PageErrorBoundary pageName="Lesson"><LessonPage /></PageErrorBoundary>,
        'cultural_note': <PageErrorBoundary pageName="Cultural Note"><CulturalNotePage /></PageErrorBoundary>,
        'quiz': <PageErrorBoundary pageName="Quiz"><QuizPage /></PageErrorBoundary>,
        'quiz_results': <PageErrorBoundary pageName="Quiz Results"><QuizResultsPage /></PageErrorBoundary>,
        'storybook': <PageErrorBoundary pageName="Storybook"><StoryPage /></PageErrorBoundary>,
        'sticker_book': <PageErrorBoundary pageName="Sticker Book"><StickerPage /></PageErrorBoundary>,
        'game_zone': <PageErrorBoundary pageName="Game Zone"><GameZonePage /></PageErrorBoundary>,
        'practice': <PageErrorBoundary pageName="Practice"><PracticePage /></PageErrorBoundary>,
            'settings': <PageErrorBoundary pageName="Settings"><SettingsPage /></PageErrorBoundary>,
        'analytics_dashboard': <PageErrorBoundary pageName="Analytics Dashboard"><AnalyticsDashboard /></PageErrorBoundary>,
        'modal_test': <PageErrorBoundary pageName="Modal Test"><ModalTestPage /></PageErrorBoundary>,
        'support_page': <PageErrorBoundary pageName="Support"><SupportPage /></PageErrorBoundary>,
        'subscription_page': <PageErrorBoundary pageName="Subscription"><SubscriptionPage /></PageErrorBoundary>,
        'post_subscribe_onboarding': <PageErrorBoundary pageName="Post Subscribe Onboarding"><PostSubscribeOnboardingPage /></PageErrorBoundary>,
        'trial_complete_transition': <PageErrorBoundary pageName="Trial Complete Transition"><TrialCompleteTransitionPage /></PageErrorBoundary>,
        'notifications': <PageErrorBoundary pageName="Notifications"><NotificationsPage /></PageErrorBoundary>,
        'unsubscribe': <PageErrorBoundary pageName="Unsubscribe"><UnsubscribePage /></PageErrorBoundary>,
        'categorySort_activity': <PageErrorBoundary pageName="Category Sort Activity"><CategorySortActivity /></PageErrorBoundary>,
        'colorMatch_activity': <PageErrorBoundary pageName="Color Match Activity"><ColorMatchActivity /></PageErrorBoundary>,
        'listenFind_activity': <PageErrorBoundary pageName="Listen Find Activity"><ListenFindActivity /></PageErrorBoundary>,
        'numberMatch_activity': <PageErrorBoundary pageName="Number Match Activity"><NumberMatchActivity /></PageErrorBoundary>,
        'sentenceScramble_activity': <PageErrorBoundary pageName="Sentence Scramble Activity"><SentenceScrambleActivity /></PageErrorBoundary>,
        'whatAmI_activity': <PageErrorBoundary pageName="What Am I Activity"><WhatAmIActivity /></PageErrorBoundary>,
        'simpleConversation_activity': <PageErrorBoundary pageName="Simple Conversation Activity"><SimpleConversationActivity /></PageErrorBoundary>,
    };

    // If we are still loading auth or progress, show the loading page.
    // This prevents the router from redirecting prematurely.
    if (isLoadingAuth || isLoadingProgress) {
        return <LoadingPage />;
    }

    // Check if page exists in pageMap, otherwise show 404
    const PageComponent = pageMap[page] || <PageErrorBoundary pageName="Not Found"><NotFoundPage /></PageErrorBoundary>;
    
    return (
        <main id="main-content" role="main" tabIndex={-1}>
            <Suspense fallback={<LoadingPage />}>
                {PageComponent}
            </Suspense>
        </main>
    );
};

export default PageRouter;
